/**
 * FR2 — explicit Video Studio UI state machine (mock 06).
 *
 * Pure derivation from the item's persisted plan (+ the optimistic `starting`
 * flag while the start request is in flight) so units/e2e can assert exact
 * states: idle → running → completed | failed.
 */

import type { AdCreativeItem, AdCreativeVideoPlan, AdCreativeVideoSegmentPlan } from '@segmently/schema-models/ad-creative';

export type VideoUiState = 'idle' | 'running' | 'completed' | 'failed';

export function deriveVideoUiState(item: AdCreativeItem, starting = false): VideoUiState {
  if (starting) return 'running';
  const plan = item.videoPlan as AdCreativeVideoPlan | undefined;
  if (!plan) return 'idle';
  if (plan.status === 'running') return 'running';
  if (plan.status === 'error') return 'failed';
  if (plan.status === 'completed') return 'completed';
  return 'idle';
}

export interface VideoPhaseRow {
  key: 'storyboard' | 'keyframes' | 'scenes' | 'stitch';
  label: string;
  status: 'done' | 'running' | 'todo';
}

/**
 * Phase checklist rows (mock 06 ②) from segment statuses + plan status.
 * Segment statuses arrive through the item doc (FR0 subscription refreshes);
 * no polling here.
 */
export function deriveVideoPhaseRows(plan: AdCreativeVideoPlan | undefined): VideoPhaseRow[] {
  const segments: AdCreativeVideoSegmentPlan[] = plan?.segments ?? [];
  const total = segments.length;
  const keyframesReady = segments.filter((segment) =>
    ['keyframe_ready', 'generating', 'completed'].includes(String(segment.status))).length;
  const scenesDone = segments.filter((segment) => segment.status === 'completed').length;
  const renderingIndex = segments.find((segment) => segment.status === 'generating')?.index;
  const planCompleted = plan?.status === 'completed';

  const storyboardDone = total > 0;
  const keyframesDone = total > 0 && keyframesReady === total;
  const scenesDoneAll = total > 0 && scenesDone === total;

  return [
    {
      key: 'storyboard',
      label: 'Storyboard',
      status: storyboardDone ? 'done' : 'running',
    },
    {
      key: 'keyframes',
      label: `Keyframes ${keyframesReady}/${total}`,
      status: keyframesDone ? 'done' : storyboardDone ? 'running' : 'todo',
    },
    {
      key: 'scenes',
      label: renderingIndex !== undefined
        ? `Scenes ${scenesDone} of ${total} · rendering scene ${renderingIndex + 1}`
        : `Scenes ${scenesDone} of ${total}`,
      status: scenesDoneAll ? 'done' : keyframesDone ? 'running' : 'todo',
    },
    {
      key: 'stitch',
      label: 'Stitch & validate',
      status: planCompleted ? 'done' : scenesDoneAll ? 'running' : 'todo',
    },
  ];
}
